/**
 * framework.js — StallBase, the stall registry, and the round lifecycle.
 *
 * A new stall needs exactly two things and touches no core file:
 *
 *   1. a module that subclasses StallBase and calls `registerStall(...)`
 *   2. one import line added to `src/stalls/index.js`
 *
 * Everything a stall needs — geometry root, physics tagging, dispensers,
 * scoreboard, timer, scoring, payout, cleanup — comes from the base class.
 * Stalls author geometry in LOCAL space where the origin is the player's feet
 * and +Z points into the booth, so a stall never knows or cares which side of
 * the midway it was placed on.
 *
 * Round lifecycle (GDD 6.1):
 *   approach → prompt → activate() [spend tickets] → first-person transition
 *   → setupRound() → attempts/timer → score events → endRound()
 *   → computeTokens() payout → "Play Again?" / "Exit" → teardown
 *
 * @module stalls/framework
 */

import * as THREE from 'three';
import { THROW_PROFILES, EVENTS } from '../constants.js';
import { bus } from '../core/events.js';
import * as physics from '../core/physics.js';
import { grab } from '../core/grab.js';
import { economy, save } from '../core/save.js';
import { hud } from '../ui/hud.js';
import { createPanel } from '../ui/panels.js';
import { audio } from '../audio/index.js';

// ─────────────────────────────────────────────────────────────────────────────
// StallBase
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Base class for every stall.
 *
 * Subclasses override the lifecycle hooks (`onBuild`, `setupRound`, `onUpdate`,
 * `onRoundEnd`, `onTeardown`) and call `addScore()` when the player does
 * something worth points.
 */
export class StallBase {
  /**
   * @param {{ spec: object, plot: object, manager: StallManager }} ctx
   */
  constructor(ctx) {
    this.spec = ctx.spec;
    this.plot = ctx.plot;
    this.manager = ctx.manager;

    this.id = this.spec.id;
    this.name = this.spec.name;
    this.cost = this.spec.cost;
    this.profile = THROW_PROFILES[this.id] || {};

    /** Physics/mesh ownership tag — everything spawned is removed by this. */
    this.tag = `stall:${this.id}`;

    /** Local-space container. Origin = player's feet, +Z = into the booth. */
    this.root = new THREE.Group();
    this.root.name = `stall-${this.id}`;
    this.plot.root.add(this.root);

    /**
     * World-space container for physics-driven meshes.
     *
     * `physics.syncMesh` writes the body's WORLD transform into
     * `mesh.position`, which Three.js interprets relative to the mesh's parent.
     * A moving mesh parented under the rotated plot group therefore renders in
     * the wrong place — invisible, usually. Dynamic meshes live here, at the
     * scene root where local and world coincide; static geometry stays in
     * `this.root` so stalls can keep authoring it in local space.
     */
    this.dynamicRoot = new THREE.Group();
    this.dynamicRoot.name = `stall-${this.id}-dynamic`;
    let node = this.plot.root;
    while (node.parent) node = node.parent;
    node.add(this.dynamicRoot);

    this.built = false;
    this.roundActive = false;
    this.score = 0;
    this.attemptsLeft = 0;
    this.maxAttempts = 0;
    this.timeLeft = 0;
    this.timed = false;

    /** Direct prizes won this round (GDD 7.4). */
    /** @type {string[]} */ this.prizesThisRound = [];
    /** Throwable handles currently available. */
    /** @type {object[]} */ this.throwables = [];
    /**
     * Scoring targets, registered by the stall so tests can inspect and move
     * them through `__cc` instead of importing this module. See registerTarget.
     * @type {Array<{id: string, group: string, handle: object}>}
     */
    this.targets = [];

    /** @type {THREE.Mesh|null} */ this.scoreboard = null;
    this._scratch = new THREE.Vector3();
  }

  // ── Hooks for subclasses ────────────────────────────────────────────────

  /** Build static geometry once. Called on the first activation. */
  onBuild() {}

  /** Prepare a fresh round: reset targets, dispense objects, set attempts. */
  setupRound() {}

  /** Per-frame while a round is active. */
  onUpdate(dt) {}

  /** Called after the round ends but before the payout is computed. */
  onRoundEnd() {}

  /** Release anything not covered by tag-based cleanup. */
  onTeardown() {}

  /**
   * Aim-assist target provider (GDD 5.3). Return the world position the throw
   * should be nudged toward, or null.
   * @param {THREE.Vector3} releasePos
   * @returns {{position: THREE.Vector3}|null}
   */
  findAimTarget(releasePos) {
    return null;
  }

  // ── Space helpers ───────────────────────────────────────────────────────

  /**
   * Local stall space → world space.
   * @param {number} x @param {number} y @param {number} z
   * @returns {THREE.Vector3}
   */
  localToWorld(x, y, z) {
    this.plot.root.updateWorldMatrix(true, false);
    return this.plot.root.localToWorld(new THREE.Vector3(x, y, z));
  }

  /**
   * World space → local stall space.
   * @param {THREE.Vector3} v
   * @returns {THREE.Vector3}
   */
  worldToLocal(v) {
    this.plot.root.updateWorldMatrix(true, false);
    return this.plot.root.worldToLocal(v.clone());
  }

  /** @returns {THREE.Quaternion} The plot's world rotation, for body poses. */
  worldQuaternion() {
    return this.plot.root.getWorldQuaternion(new THREE.Quaternion());
  }

  /**
   * Rotate a local-space direction into world space.
   * @param {THREE.Vector3|number[]} dir
   * @returns {THREE.Vector3}
   */
  localToWorldDirection(dir) {
    const v = Array.isArray(dir) ? new THREE.Vector3(dir[0], dir[1], dir[2]) : dir.clone();
    return v.applyQuaternion(this.worldQuaternion());
  }

  // ── Spawning ────────────────────────────────────────────────────────────

  /**
   * Add a mesh to the stall at a local position and give it a physics body.
   * Everything created this way is tagged and torn down automatically.
   *
   * @param {THREE.Object3D} mesh Usually straight from assets3d/factory.
   * @param {{x:number,y:number,z:number}|number[]} localPos
   * @param {import('../core/physics.js').BodyDesc} [desc] Merged over
   *   `mesh.userData.collider`.
   * @returns {import('../core/physics.js').BodyHandle}
   */
  spawn(mesh, localPos, desc = {}) {
    const p = Array.isArray(localPos)
      ? { x: localPos[0], y: localPos[1], z: localPos[2] }
      : localPos;

    const collider = mesh.userData.collider || {};
    const merged = { ...collider, ...desc };
    const isStatic = merged.type === 'fixed';

    const worldPos = desc.position
      ? new THREE.Vector3(desc.position.x, desc.position.y, desc.position.z)
      : this.localToWorld(p.x, p.y, p.z);
    const worldQuat = desc.rotation || this.worldQuaternion();

    if (isStatic) {
      // Static geometry stays in local space so stalls author it naturally.
      mesh.position.set(p.x, p.y, p.z);
      this.root.add(mesh);
    } else {
      // Physics drives this mesh, so it must live where world == local.
      mesh.position.copy(worldPos);
      mesh.quaternion.copy(worldQuat);
      this.dynamicRoot.add(mesh);
    }
    mesh.updateWorldMatrix(true, false);

    return physics.addBody(mesh, {
      ...merged,
      tag: this.tag,
      position: worldPos,
      rotation: worldQuat,
      userData: { stallId: this.id, ...(collider.userData || {}), ...(desc.userData || {}) },
    });
  }

  /**
   * Spawn a throwable and register it with the grab system.
   * @param {THREE.Object3D} mesh
   * @param {{x,y,z}|number[]} localPos
   * @param {import('../core/physics.js').BodyDesc & {holdOffset?: THREE.Vector3, kind?: string}} [desc]
   * @returns {import('../core/physics.js').BodyHandle}
   */
  spawnThrowable(mesh, localPos, desc = {}) {
    const handle = this.spawn(mesh, localPos, desc);
    handle.userData.throwable = true;
    handle.userData.thrown = false;
    grab.register(handle, {
      kind: desc.kind || mesh.name,
      holdOffset: desc.holdOffset || mesh.userData.holdOffset || null,
      onRelease: () => this._onThrowableReleased(handle),
    });
    this.throwables.push(handle);
    return handle;
  }

  /** @private */
  _onThrowableReleased(handle) {
    if (handle.userData.thrown) return;
    handle.userData.thrown = true;
    save.data.stats.throwsMade += 1;
    this.consumeAttempt();
    audio.play('throw-release');
  }

  /**
   * A tray of objects within arm's reach (GDD 6.1 "Object Dispenser").
   * @param {number} count
   * @param {() => THREE.Object3D} make Factory for each object.
   * @param {{ y?: number, z?: number, spacing?: number, desc?: object }} [opts]
   * @returns {object[]} The spawned handles.
   */
  dispense(count, make, opts = {}) {
    const y = opts.y ?? 1.0;
    const z = opts.z ?? 0.42;
    const spacing = opts.spacing ?? 0.17;
    const out = [];
    for (let i = 0; i < count; i++) {
      const x = (i - (count - 1) / 2) * spacing;
      out.push(this.spawnThrowable(make(), { x, y, z }, opts.desc || {}));
    }
    return out;
  }

  /**
   * Build the tray the dispensed objects rest on.
   * @param {{ width?: number, y?: number, z?: number }} [opts]
   */
  buildTray(opts = {}) {
    const width = opts.width ?? 0.72;
    const y = opts.y ?? 0.94;
    const z = opts.z ?? 0.42;
    const tray = new THREE.Mesh(
      new THREE.BoxGeometry(width, 0.06, 0.3),
      new THREE.MeshStandardMaterial({ color: 0x7a5334, roughness: 0.9 }),
    );
    tray.castShadow = true;
    tray.receiveShadow = true;
    this.spawn(tray, { x: 0, y, z }, {
      type: 'fixed', shape: 'box', halfExtents: { x: width / 2, y: 0.03, z: 0.15 },
    });
    return tray;
  }

  // ── Scoreboard ──────────────────────────────────────────────────────────

  /**
   * Mount a live scoreboard on the booth.
   * @param {{ x?: number, y?: number, z?: number, width?: number, height?: number }} [opts]
   * @returns {THREE.Mesh}
   */
  buildScoreboard(opts = {}) {
    const panel = createPanel({
      title: this.name.toUpperCase(),
      width: opts.width ?? 1.5,
      height: opts.height ?? 0.85,
      lines: ['Score  0', ''],
      align: 'left',
    });
    // Deep enough into the booth to read comfortably rather than sitting in
    // the player's face at the counter.
    panel.position.set(opts.x ?? 0, opts.y ?? 2.4, opts.z ?? 1.9);
    panel.rotation.y = Math.PI;
    this.root.add(panel);
    this.scoreboard = panel;
    return panel;
  }

  /** Refresh the scoreboard text. Override for a stall-specific layout. */
  updateScoreboard() {
    if (!this.scoreboard) return;
    const lines = [`Score  ${this.score}`];
    if (this.timed) lines.push(`Time   ${Math.ceil(Math.max(0, this.timeLeft))}s`);
    else if (this.maxAttempts) lines.push(`Balls  ${this.attemptsLeft} / ${this.maxAttempts}`);
    this.scoreboard.setLines(lines);
  }

  // ── Scoring ─────────────────────────────────────────────────────────────

  /**
   * Award points and announce them.
   * @param {number} points
   * @param {{ position?: THREE.Vector3, label?: string, color?: string }} [opts]
   *   `position` is WORLD space; pass `localToWorld(...)` output.
   */
  addScore(points, opts = {}) {
    if (!this.roundActive) return;
    this.score += points;
    bus.emit(EVENTS.STALL_SCORE, {
      stallId: this.id,
      name: this.name,
      points,
      total: this.score,
      attemptsLeft: this.timed ? null : this.attemptsLeft,
      label: opts.label || null,
    });
    if (opts.position) {
      hud.scorePopup(points > 0 ? `+${points}` : String(points), opts.position, {
        color: opts.color || (points > 0 ? '#ffd166' : '#ff8f8f'),
      });
    }
    audio.play('score-popup', { volume: Math.min(1, 0.4 + points / 500) });
    this.updateScoreboard();
  }

  /**
   * Award a direct prize (GDD 7.4) immediately.
   * @param {'plush-small'|'plush-large'|'fish'} kind
   * @param {{ variant?: string }} [opts]
   */
  awardDirectPrize(kind, opts = {}) {
    const record = economy.awardDirectPrize({ kind, stallId: this.id, ...opts });
    this.prizesThisRound.push(kind);
    audio.play('prize-earned');
    return record;
  }

  // ── Scoring targets ─────────────────────────────────────────────────────

  /**
   * Declare a body as a scoring target.
   *
   * This is what lets a test inspect and reposition a stall's targets through
   * `__cc.stalls.targets()` / `moveTarget()` without importing this module —
   * reaching into `/src/...` from a test only works against the dev server and
   * is how you end up with duplicate module singletons.
   *
   * Mark a target scored by setting `handle.userData.scored = true` when it
   * counts, so tests can assert on it.
   *
   * @param {import('../core/physics.js').BodyHandle} handle
   * @param {{ id?: string, group?: string }} [opts] `group` lets a test select
   *   a subset, e.g. the 'mid' pyramid.
   * @returns {string} The target's id.
   */
  registerTarget(handle, { id, group = 'target' } = {}) {
    const targetId = id || `${group}-${this.targets.length}`;
    handle.userData.targetId = targetId;
    handle.userData.targetGroup = group;
    handle.userData.scored = false;
    this.targets.push({ id: targetId, group, handle });
    return targetId;
  }

  /**
   * @returns {Array<{id, group, position, localPosition, scored}>} Live targets,
   *   positions in world AND stall-local space.
   */
  listTargets() {
    const out = [];
    for (const t of this.targets) {
      if (t.handle.removed) continue;
      const world = t.handle.position();
      out.push({
        id: t.id,
        group: t.group,
        position: world.toArray(),
        localPosition: this.worldToLocal(world).toArray(),
        scored: !!t.handle.userData.scored,
      });
    }
    return out;
  }

  /**
   * Reposition a target in stall-local space and stop it dead. Used by tests to
   * stage a scoring situation that physics alone cannot be relied on to produce.
   * @param {string} targetId
   * @param {number[]} localPos
   * @param {number[]} [rotation] Optional world-space quaternion [x,y,z,w].
   * @returns {boolean}
   */
  moveTarget(targetId, localPos, rotation) {
    const t = this.targets.find((e) => e.id === targetId);
    if (!t || t.handle.removed) return false;
    t.handle.setPosition(this.localToWorld(localPos[0], localPos[1], localPos[2]));
    if (rotation) {
      t.handle.setRotation({
        x: rotation[0], y: rotation[1], z: rotation[2], w: rotation[3],
      });
    }
    t.handle.freeze();
    return true;
  }

  /** Consume one attempt; ends the round when they run out. */
  consumeAttempt() {
    if (this.timed || !this.roundActive) return;
    this.attemptsLeft = Math.max(0, this.attemptsLeft - 1);
    bus.emit(EVENTS.STALL_ATTEMPT, {
      stallId: this.id, attemptsLeft: this.attemptsLeft, maxAttempts: this.maxAttempts,
    });
    this.updateScoreboard();
  }

  /**
   * Configure the round format.
   * @param {{ attempts?: number, seconds?: number }} opts
   */
  setRoundFormat({ attempts = 0, seconds = 0 }) {
    this.maxAttempts = attempts;
    this.attemptsLeft = attempts;
    this.timed = seconds > 0;
    this.timeLeft = seconds;
  }

  /** End the round. Safe to call more than once. */
  endRound() {
    if (!this.roundActive) return;
    this.roundActive = false;
    this.manager._finishRound(this);
  }

  // ── Internals used by the manager ───────────────────────────────────────

  /** @internal */
  _build() {
    if (this.built) return;
    this.onBuild();
    this.built = true;
  }

  /** @internal */
  _beginRound() {
    this.score = 0;
    this.prizesThisRound = [];
    this.throwables = [];
    this.targets = [];
    this.roundActive = true;
    this.setupRound();
    this.updateScoreboard();
    bus.emit(EVENTS.STALL_ROUND_START, {
      stallId: this.id, name: this.name, cost: this.cost,
      attempts: this.maxAttempts, seconds: this.timeLeft,
    });
  }

  /** @internal */
  _update(dt) {
    if (this.roundActive && this.timed) {
      this.timeLeft -= dt;
      if (this.timeLeft <= 0) {
        this.timeLeft = 0;
        this.updateScoreboard();
        this.endRound();
        return;
      }
    }
    this.onUpdate(dt);
  }

  /**
   * Remove every body, mesh and grabbable this stall created.
   * Called on exit — this is the discipline that keeps three rounds from
   * leaking bodies.
   * @internal
   */
  _teardown() {
    this.onTeardown();
    for (const h of this.throwables) grab.unregister(h);
    this.throwables = [];
    this.targets = [];
    physics.removeByTag(this.tag);
    // Anything left over that had no body of its own (decor, panels, prizes).
    for (const container of [this.root, this.dynamicRoot]) {
      while (container.children.length > 0) {
        const child = container.children[0];
        container.remove(child);
        if (child.dispose) child.dispose();
      }
    }
    if (this.scoreboard) {
      this.scoreboard = null;
    }
    this.built = false;
    this.roundActive = false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Re-exports — the public surface of the stalls layer.
//
// Stall modules and tests import from THIS file (or from `./index.js`); the
// registry and the round manager are split into their own modules only to keep
// each file readable. These paths are the stable contract.
// ─────────────────────────────────────────────────────────────────────────────

export { registerStall, isRegistered, registeredStalls } from './registry.js';
export { stallManager } from './manager.js';
